import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { InlineLoader } from '../../components/common/Loader';
import { getDashboardStats } from '../../services/adminService';
import { formatCurrency } from '../../utils/helpers';

function StatCard({ label, value, icon, color, bg, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`card-p flex items-center gap-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    >
      <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
        <i className={`fa-solid ${icon} ${color} text-lg`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-text-primary font-heading">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate        = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardStats()
      .then(res => setStats(res.stats))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout title="Dashboard">
      {loading ? <InlineLoader text="Loading stats..." /> : stats && (
        <div className="space-y-6">
          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard
              label="Total Doctors"
              value={stats.totalDoctors}
              icon="fa-user-doctor"
              color="text-primary"
              bg="bg-primary-light"
              onClick={() => navigate('/admin/doctors')}
            />
            <StatCard
              label="Total Patients"
              value={stats.totalPatients}
              icon="fa-users"
              color="text-info"
              bg="bg-blue-50"
              onClick={() => navigate('/admin/patients')}
            />
            <StatCard
              label="Today's Appointments"
              value={stats.todayAppointments}
              icon="fa-calendar-day"
              color="text-success"
              bg="bg-green-50"
              onClick={() => navigate('/admin/appointments')}
            />
            <StatCard
              label="Pending Payments"
              value={stats.pendingPayments}
              icon="fa-clock"
              color="text-accent"
              bg="bg-amber-50"
              onClick={() => navigate('/admin/payments')}
            />
            <StatCard
              label="Platform Revenue"
              value={formatCurrency(stats.platformRevenue)}
              icon="fa-chart-line"
              color="text-success"
              bg="bg-green-50"
              onClick={() => navigate('/admin/revenue')}
            />
          </div>

          {/* Quick actions */}
          <div>
            <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Approve Payments',    icon: 'fa-check-circle',   color: 'text-success', path: '/admin/payments',     desc: `${stats.pendingPayments} pending` },
                { label: 'Verify Doctors',       icon: 'fa-user-check',     color: 'text-primary', path: '/admin/doctors?verified=false', desc: 'Review new signups' },
                { label: 'All Appointments',     icon: 'fa-calendar-alt',   color: 'text-info',    path: '/admin/appointments', desc: "Today's bookings" },
                { label: 'App Settings',         icon: 'fa-gear',           color: 'text-text-secondary', path: '/admin/settings', desc: 'Logo, fees, API keys' },
              ].map(item => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="card-p text-left hover:shadow-md transition-shadow flex items-start gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-surface flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`fa-solid ${item.icon} ${item.color} text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{item.label}</p>
                    <p className="text-xs text-text-muted mt-0.5">{item.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
